import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TermsSection = () => {
  const termsHighlights = [
    {
      title: 'Service Scope',
      icon: 'Package',
      points: [
        'Cloud-based employee onboarding platform',
        'AI-powered assistance and workflow automation',
        '99.99% uptime SLA for Enterprise plans',
        'Unlimited users on Growth and Enterprise tiers'
      ]
    },
    {
      title: 'User Responsibilities',
      icon: 'Users',
      points: [
        'Maintain account security and credentials',
        'Comply with acceptable use policies',
        'Ensure data accuracy and legal compliance',
        'Respect intellectual property rights'
      ]
    },
    {
      title: 'Payment Terms',
      icon: 'CreditCard',
      points: [
        'Annual billing with 14-day money-back guarantee',
        'Automatic renewal unless canceled 30 days prior',
        'Price lock guarantee for annual contracts',
        'Prorated refunds for early Enterprise cancellations'
      ]
    },
    {
      title: 'Liability & Warranties',
      icon: 'ShieldCheck',
      points: [
        'Service provided "as-is" with regular updates',
        'Limited liability up to 12 months of fees paid',
        'Indemnification for IP infringement claims',
        'Force majeure provisions for service interruptions'
      ]
    },
  ];

  const versionHistory = [
    { version: 'v3.2', date: 'January 15, 2025', changes: 'Updated GDPR compliance clauses' },
    { version: 'v3.1', date: 'October 10, 2024', changes: 'Added AI usage transparency section' },
    { version: 'v3.0', date: 'July 1, 2024', changes: 'Major revision for enterprise features' },
  ];

  return (
    <section id="terms" className="scroll-mt-20">
      <div className="bg-card/30 backdrop-blur-sm border border-border rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
            <Icon name="FileText" size={32} className="text-primary" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-foreground">Terms of Service</h2>
            <p className="text-muted-foreground mt-1">Version 3.2 — Effective January 15, 2025</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none mb-8">
          <p className="text-lg text-muted-foreground leading-relaxed">
            These Terms of Service govern your access to and use of First On Date services. By using our platform, you agree to be bound by these terms. For enterprise custom agreements, contact sales@firstondate.com
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {termsHighlights?.map((section, index) => (
            <div 
              key={index}
              className="bg-background/50 border border-border rounded-lg p-6 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={section?.icon} size={20} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground">{section?.title}</h3>
              </div>
              <ul className="space-y-2">
                {section?.points?.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Version History */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="History" size={20} />
            Version History
          </h3>
          <div className="space-y-3">
            {versionHistory?.map((version, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-4 bg-background/50 border border-border rounded-lg hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <div>
                    <div className="font-semibold text-foreground">{version?.version}</div>
                    <div className="text-sm text-muted-foreground">{version?.date}</div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">{version?.changes}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Legal Clauses */}
        <div className="p-6 bg-warning/5 border border-warning/20 rounded-lg mb-8">
          <div className="flex items-start gap-4">
            <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-foreground mb-2">Arbitration & Dispute Resolution</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Disputes resolved through binding arbitration under International Chamber of Commerce rules. Enterprise customers may opt for exclusive jurisdiction in courts of Belgium.
              </p>
              <h3 className="font-semibold text-foreground mb-2">Termination Rights</h3>
              <p className="text-sm text-muted-foreground">
                Either party may terminate with 30 days notice. We may suspend service immediately for violation of acceptable use policy or non-payment. Data export available for 30 days post-termination.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <Button 
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={() => window.open('/terms-of-service.pdf', '_blank')}
          >
            Download Full Terms (PDF)
          </Button>
          <Button 
            variant="outline"
            iconName="Scale"
            iconPosition="left"
            onClick={() => window.open('mailto:legal@firstondate.com', '_blank')}
          >
            Contact Legal Team
          </Button>
          <Button 
            variant="ghost"
            iconName="Eye"
            iconPosition="left"
            onClick={() => window.open('/terms-comparison', '_blank')}
          >
            Compare with v3.1
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TermsSection;